// DoctrineSection — shared UI for Probability + Value doctrine fallback picks.
//
// Mounts below T1 picks (or alone when T1 is empty). Every product page that
// wants to kill its empty screen imports this and passes doctrine picks from
// the useDoctrinepicks hook. Pure presentation — no data fetch.
//
// DOCTRINE GUARDRAILS:
//   - No staking CTA. Ever. Intelligence-only display.
//   - Transparent labelling: users always know these are NOT edge-backed picks.
//   - Copy is outcome-language only (no methodology vocab).

import type { CSSProperties } from "react";
import { SubstrateEmptyState } from "./SubstrateEmptyState.tsx";
import type { SubstrateWeekStatus } from "../doctrine/useSubstrateStatus.ts";

// ─── Types ───────────────────────────────────────────────────────────

export interface DoctrinePick {
  id: string;
  doctrine: "probability" | "value";
  fixture: string;
  league: string;
  kickoffDisplay: string;
  market: string;
  selection: string;
  offeredOdds: number;
  fairProb: number;
  payoutRatio?: number;
  reasoning: string;
}

export interface DoctrineSectionProps {
  probabilityPicks: DoctrinePick[];
  valuePicks: DoctrinePick[];
  /** Optional: true when the product had ZERO T1 picks (show the full
   *  transparency banner). When false, shows a lighter "also watching" header. */
  isFullFallback?: boolean;
  /** When true AND both doctrines are empty, render the substrate-level
   *  empty state (absolute-zero: no fixtures priced this week). */
  substrateEmpty?: boolean;
  /** Substrate week metadata for the absolute-zero state display. */
  substrateWeek?: SubstrateWeekStatus | null;
}

// ─── Styles ──────────────────────────────────────────────────────────

const sectionRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
  marginTop: 28,
};

const bannerStyle: CSSProperties = {
  background: "var(--bg-elev-1, #111)",
  border: "1px solid var(--border-faint, #2a2a2a)",
  borderRadius: 10,
  padding: "20px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const bannerTitle: CSSProperties = {
  fontFamily: "var(--font-head, inherit)",
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color: "var(--text-primary, #fff)",
  margin: 0,
};

const bannerBody: CSSProperties = {
  fontFamily: "var(--font-body, inherit)",
  fontSize: 13,
  lineHeight: 1.6,
  color: "var(--text-secondary, #999)",
  margin: 0,
};

const doctrineGroupHeader: CSSProperties = {
  fontFamily: "var(--font-mono, monospace)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  color: "var(--text-tertiary, #666)",
  margin: "12px 0 4px",
};

const cardStyle: CSSProperties = {
  background: "var(--bg-elev-1, #111)",
  border: "1px solid var(--border-faint, #2a2a2a)",
  borderRadius: 10,
  padding: "14px 18px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const probCard: CSSProperties = {
  ...cardStyle,
  borderLeft: "3px solid var(--owl-cyan, #5ecfcf)",
};

const valueCard: CSSProperties = {
  ...cardStyle,
  borderLeft: "3px solid var(--owl-violet, #a78bfa)",
};

const badgeBase: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontFamily: "var(--font-mono, monospace)",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  padding: "3px 8px",
  borderRadius: 4,
};

const probBadge: CSSProperties = {
  ...badgeBase,
  background: "rgba(94,207,207,0.10)",
  color: "var(--owl-cyan, #5ecfcf)",
  border: "1px solid var(--owl-cyan, #5ecfcf)",
};

const valueBadge: CSSProperties = {
  ...badgeBase,
  background: "rgba(167,139,250,0.10)",
  color: "var(--owl-violet, #a78bfa)",
  border: "1px solid var(--owl-violet, #a78bfa)",
};

const fixtureName: CSSProperties = {
  fontFamily: "var(--font-body, inherit)",
  fontSize: 14,
  fontWeight: 600,
  color: "var(--text-primary, #fff)",
};

const metaLine: CSSProperties = {
  fontFamily: "var(--font-mono, monospace)",
  fontSize: 11,
  color: "var(--text-tertiary, #666)",
  letterSpacing: "0.04em",
};

const oddsChip: CSSProperties = {
  fontFamily: "var(--font-mono, monospace)",
  fontSize: 13,
  fontWeight: 700,
  color: "var(--text-primary, #fff)",
  background: "var(--bg-elev-2, #1a1a1a)",
  padding: "4px 10px",
  borderRadius: 6,
  display: "inline-block",
};

const reasoningText: CSSProperties = {
  fontFamily: "var(--font-body, inherit)",
  fontSize: 12,
  lineHeight: 1.55,
  color: "var(--text-secondary, #999)",
  fontStyle: "italic",
  margin: 0,
};

const disclaimerText: CSSProperties = {
  fontFamily: "var(--font-body, inherit)",
  fontSize: 11,
  color: "var(--text-tertiary, #666)",
  fontStyle: "italic",
  margin: 0,
};

// ─── Components ──────────────────────────────────────────────────────

function DoctrineCard({ pick }: { pick: DoctrinePick }) {
  const isProb = pick.doctrine === "probability";
  const card = isProb ? probCard : valueCard;
  const badge = isProb ? probBadge : valueBadge;
  const label = isProb ? "PROBABILITY" : "VALUE";

  return (
    <article style={card} data-doctrine={pick.doctrine}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={badge}>{label} DOCTRINE</span>
        <span style={metaLine}>{pick.kickoffDisplay}</span>
      </header>

      <div style={fixtureName}>{pick.fixture}</div>
      <div style={metaLine}>{pick.league}</div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 2 }}>
        <span style={metaLine}>{pick.market}</span>
        <span style={{ ...fixtureName, fontSize: 13 }}>{pick.selection}</span>
        <span style={oddsChip}>{pick.offeredOdds.toFixed(2)}</span>
        {pick.doctrine === "value" && pick.payoutRatio != null && (
          <span style={{ ...metaLine, color: "var(--owl-violet, #a78bfa)" }}>
            {(pick.payoutRatio * 100 - 100).toFixed(0)}% above fair
          </span>
        )}
      </div>

      <p style={reasoningText}>{pick.reasoning}</p>
      <p style={disclaimerText}>
        {isProb
          ? "This is not an edge-backed pick. The model favours this outcome but found no exploitable pricing gap."
          : "This is not an edge-backed pick. The odds here are the most generous relative to assessed probability."}
      </p>
    </article>
  );
}

function DoctrineGroup({
  title,
  subtitle,
  picks,
}: {
  title: string;
  subtitle: string;
  picks: DoctrinePick[];
}) {
  if (picks.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div>
        <h4 style={doctrineGroupHeader}>{title}</h4>
        <p style={{ ...disclaimerText, marginTop: 2 }}>{subtitle}</p>
      </div>
      {picks.map((p) => (
        <DoctrineCard key={p.id} pick={p} />
      ))}
    </div>
  );
}

// ─── Main Section ────────────────────────────────────────────────────

export function DoctrineSection({
  probabilityPicks,
  valuePicks,
  isFullFallback = true,
  substrateEmpty = false,
  substrateWeek,
}: DoctrineSectionProps) {
  const hasAny = probabilityPicks.length > 0 || valuePicks.length > 0;
  if (!hasAny && substrateEmpty) {
    return <SubstrateEmptyState week={substrateWeek ?? null} />;
  }
  if (!hasAny) return null;

  return (
    <section style={sectionRoot}>
      <div style={bannerStyle}>
        <h3 style={bannerTitle}>
          {isFullFallback ? "Doctrine Intelligence" : "Also watching"}
        </h3>
        <p style={bannerBody}>
          {isFullFallback
            ? "Our agents scanned the full fixture list and found no exploitable edge today. " +
              "Below are two alternative reads — where the probability leans strongest, and " +
              "where the odds pay best relative to the true probability. These are intelligence " +
              "only — STRIKZA does not recommend staking without a confirmed edge."
            : "Additional fixture intelligence from the probability and value doctrines. " +
              "These are not edge-backed picks."}
        </p>
      </div>

      <DoctrineGroup
        title="Probability Doctrine"
        subtitle="Where the math leans — highest probability selections with no exploitable edge."
        picks={probabilityPicks}
      />

      <DoctrineGroup
        title="Value Doctrine"
        subtitle="Where the odds are kindest — best payout-to-probability ratio with no exploitable edge."
        picks={valuePicks}
      />
    </section>
  );
}
